import React, { useEffect, useMemo, useState } from 'react';
import TwDialog from '../../TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../TwFields/TwFields';
import DocumentUtils from '../../../utils/documentUtils';
import TwSelect, { TwSelectOption } from '../../TwSelects/TwSelects';
import { TwButton, TwButtonType } from '../../TwButtons/TwButtons';
import './EditFiles.scss';
import GuestService from '../../../providers/Guest/Guest.service';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import { OrderAttachment, OrdersTripsDocumentParams } from '../../../redux/api/orders/orders.model';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useUpdateOrderTripDocumentByIdMutation } from '../../../redux/api/orders/ordersApi';
import { LoadDocumentType } from '../../../redux/api/load/load.model';
import { useAppSelector } from '../../../redux/hooks';

export interface EditFileProps {
  open: boolean;
  document?: OrderAttachment;
  entityList: TwSelectOption[];
  entity: 'order' | 'trip';
  title?: string;
  guestServiceToken?: string;
  order: { id: string; readable_id: string | number };
  onClickCLose: () => void;
  onEditSuccess: () => void;
  entityStatus?: string;
}

const EditFile: React.FC<EditFileProps> = ({
  open,
  title = 'Edit document',
  guestServiceToken,
  document,
  entity,
  entityList,
  order,
  onClickCLose,
  onEditSuccess,
  entityStatus
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const activeTrips = useAppSelector((state) => state.activeTrips);
  const { addNotification } = useNotifications();
  const [updateOrderTripDocumentById] = useUpdateOrderTripDocumentByIdMutation();
  const [tripId, setTripId] = useState<string | undefined>(document?.trip_id);
  const [orderId, setOrderId] = useState<string | undefined>(document?.order_id);
  const [type, setType] = useState<LoadDocumentType | undefined>(document?.type);
  const [visibility, setVisibility] = useState<boolean>(document?.visibility || false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (document && open) {
      setTripId(document.trip_id || '');
      setOrderId(document.order_id);
      setType(document.type);
      setVisibility(document.visibility);
    }
  }, [document, open]);

  const handleSubmit = (): void => {
    setLoading(true);
    const valuesToUpdate: Partial<OrdersTripsDocumentParams> = {
      type: type || LoadDocumentType.other,
      visibility: visibility,
      name: document?.attachment?.name
    };

    if (tripId && tripId !== '') {
      valuesToUpdate.tripId = tripId;
    } else if (tripId === '' && document?.trip_id) {
      valuesToUpdate.tripId = null;
    }

    if (orderId && orderId !== '') {
      valuesToUpdate.orderId = orderId;
    } else if (orderId === '' && document?.order_id) {
      valuesToUpdate.orderId = null;
    }

    /*  if (guestServiceToken) {
      GuestService.updateDocument(guestServiceToken, {
        id,
        type: values.type
      } as Partial<OrderAttachment>);
    } */
    updateOrderTripDocumentById({
      data: valuesToUpdate,
      id: document?.id as string,
      searchTripParams: activeTrips,
      status: entityStatus
    })
      .unwrap()
      .then((res) => {
        addNotification({
          message: 'Document updated successfully',
          variant: 'success'
        });
        onEditSuccess();
      })
      .catch((error) => {
        console.error(error);
        logRocketTrack('Edit document', {
          action: 'Edit document type or visibility',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: error.data.message || 'Error updating document',
          variant: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const documentTypeList = DocumentUtils.getDocTypeOptions();
  const visibilityOptions = DocumentUtils.getVisibilityOptions();

  const defaultTripOption = useMemo(() => {
    const option = {
      label: '-',
      value: ''
    };

    if (document && document.trip_tag && document.trip_id) {
      option.label = `${order.readable_id} - ${document.trip_tag.trip_sort_order + 1}`;
      option.value = document.trip_id;
    }
    return option;
  }, [document]);

  return (
    <TwDialog
      open={open}
      title={title}
      onClickClose={onClickCLose}
      actions={[
        <TwButton
          key="1"
          fullwidth={false}
          label="Cancel"
          type={TwButtonType.secondary}
          action={onClickCLose}
          disabled={loading}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          label="Save"
          type={TwButtonType.primary}
          action={handleSubmit}
          disabled={loading}
        />
      ]}
    >
      <TwField
        label="Name"
        name="fileName"
        inputType={TwInputTypes.text}
        fullWidth={true}
        autoComplete="off"
        value={(document && document.attachment?.name) || ''}
        disabled={true}
        className="edit-file__file-name-disabled"
      />

      <Stack className="edit-file__select-container">
        <Box>
          <TwSelect
            label={`Order # ${entity === 'trip' ? '(optional)' : ''}`}
            name="orderId"
            value={orderId}
            optionList={
              entity === 'order'
                ? [{ value: order.id, label: order.readable_id as string }]
                : entityList
            }
            onInputChange={(e): void => {
              setOrderId(e.target.value);
            }}
            fullWidth={true}
            disabled={entity === 'order'}
          />
        </Box>
        <Box>
          <TwSelect
            label={`Trip # ${entity === 'order' ? '(optional)' : ''}`}
            name="tripId"
            value={tripId}
            optionList={entity === 'trip' ? [defaultTripOption] : entityList}
            onInputChange={(e): void => {
              setTripId(e.target.value);
            }}
            fullWidth={true}
            disabled={entity === 'trip'}
          />
        </Box>
      </Stack>

      <Stack className="edit-file__select-container">
        <Box>
          <TwSelect
            label="Document type (optional)"
            name="type"
            placeholder="Select type"
            value={type}
            optionList={documentTypeList}
            onInputChange={(e): void => {
              setType(e.target.value as LoadDocumentType);
            }}
            sx={{ marginTop: '30px' }}
          />
        </Box>
        <Box>
          <TwSelect
            label="Visibility"
            name="visible"
            value={visibility ? 'visible' : 'hidden'}
            optionList={visibilityOptions}
            onInputChange={(e): void => {
              setVisibility(e.target.value === 'visible');
            }}
            sx={{ marginTop: '30px' }}
          />
        </Box>
      </Stack>
    </TwDialog>
  );
};

export default EditFile;
